import { CommonModule } from '@angular/common';
import { Component, Input, NgModule, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { Observable } from 'rxjs';

import { TranslateService } from '@codeandweb/ngx-translate';

import { StateService, UIRouterGlobals, UIRouterModule } from '@uirouter/angular';

import { CalendarModule } from '@app/primeng-overrides/calendar';
import { MenuModule } from '@app/primeng-overrides/menu';
import { Table, TableModule } from '@app/primeng-overrides/table';
import { TableLazyLoadEvent } from '@app/primeng-overrides/table/table.interface';
import { AccordionModule } from 'primeng/accordion';
import { MenuItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogService } from 'primeng/dynamicdialog';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { PanelModule } from 'primeng/panel';
import { ToolbarModule } from 'primeng/toolbar';
import { TooltipModule } from 'primeng/tooltip';

import { Abonne } from '@app/abonne/abonne.model';
import { AbonneService } from '@app/abonne/abonne.service';
import { FamilleSelectorModule } from '@app/famille/famille-selector';
import { NomenclatureSelectorModule } from '@app/nomenclature/nomenclature-selector';
import { Nomenclature, NomenclatureElementKey } from '@app/nomenclature/nomenclature.model';
import { extractNomenclatureElementIdFromKey, NomenclatureNodeData } from '@app/nomenclature/nomenclature.service';
import { DropdownModule } from '@app/primeng-overrides/dropdown';
import { RubriqueSelectorModule } from '@app/rubrique/rubrique-selector';
import { Site } from '@app/site/site.model';
import { SiteService } from '@app/site/site.service';
import { CommentaireTexteDialogComponent } from '@app/texte/commentaire-texte-dialog';
import { EvaluationSelectorModule } from '@app/texte/evaluation-selector';
import { StatutRecolementSelectorModule } from '@app/texte/statut-recolement-selector';
import { CodeEvaluation, StatutRecolement, Texte, TexteEvaluation } from '@app/texte/texte.model';
import { ThemeSelectorModule } from '@app/theme/theme-selector';
import { Utilisateur } from '@app/utilisateur/utilisateur.model';
import { GlobalModule } from '@global/global.module';
import { StorageService } from '@global/storage.service';

import { environment } from '@environments/environment';
import { createDownloadFromHttpResponse, simpleDateToString, startOfDay } from '@helpers/utils';

@Component({
	selector: 'liste-textes-conformite',
	templateUrl: `liste-textes-conformite.html`
})
export class ListeTextesConformiteComponent implements OnInit {

	@ViewChild('tableTextes', { static: true }) table: Table;

	@Input() me: Utilisateur;
	@Input() sites: Site[];
	@Input() etapeConformite: ('recolements' | 'evaluations') = 'recolements';

	comingFromDashboard: boolean;
	@Input('selected_sta_code') initialStaCode?: StatutRecolement;
	@Input('selected_article_eva_code') initialArticleEvaCode?: CodeEvaluation;
	@Input('select_unstatused_texts') select_unstatused_texts: any;
	@Input('nomenclature_selection_key') nomenclature_selection_key?: NomenclatureElementKey; 

	abonne_courant: Abonne;

	siteNames: string;
	siteIds: number[];
	mergedNomenclatureDiagnostics: Nomenclature;

	canEdit = false;

	values: TexteEvaluation[] = [];
	pristineValues: TexteEvaluation[] = [];
	totalRecords: number = 0;
	loading: boolean;
	exportLoading: boolean = false;
	rows: number = environment.default_rows;
	expandedRows: any = {};

	remainingRecolementsArticleBySiteIdByTextId: Record</* sit_id*/ number, Record<number, number>> = {};

	// Paramètres et filtres

	_defaultParams: { [key: string]: any } = {
		search: null,
		search_reference: null,
		fam_id: null,
		thm_id: null,
		rub_id: null,
		order_by: 'txt_date_signature',
		sta_code: null,
		eva_code: null,
		eva_code_article: null,
		hors_diagnostic: null,
		prive: null,
		abroge: null,
		date_debut_reevaluation: null,
		date_fin_reevaluation: null,
		date_debut_sortie: null,
		date_fin_sortie: null,
		a_reevaluer: null,
		non_statues: null
	};
	params = structuredClone(this._defaultParams);
	showAdvancedFilters: boolean = false;
	orderByOptions: {label: string, value: string | null, disabled?: boolean}[];
	nomenclatureFilter: NomenclatureNodeData | null = null;
	today: Date = startOfDay(new Date());
	dateReevaluationRange: Date[] | null = null;
	dateSortieRange: Date[] | null = null;

	// Exports

	menuItems: MenuItem[];

	constructor(
		private storageService: StorageService,
		private stateService: StateService,
		private translate: TranslateService,
		private dialogService: DialogService,
		private siteService: SiteService,
		private abonneService: AbonneService,
		private uiRouterGlobals: UIRouterGlobals,
	) {
		this.abonne_courant = this.abonneService.currentAbonneValue!;
		this.orderByOptions = [
			{
				value: 'txt_date_signature',
				label: this.translate.instant('textes.date_signature')
			},
			{
				value: null,
				label: this.translate.instant('common.alphabetical_order'),
			},
			{
				value: 'pertinence',
				label: this.translate.instant('textes.sort_option_relevance'),
			},
		
			{
				value: 'txt_date_application',
				label: this.translate.instant('textes.date_application')
			},
			{
				value: 'txt_date_modificative',
				label: this.translate.instant('textes.date_modificative')
			},
			{
				value: 'txt_date_abrogation',
				label: this.translate.instant('textes.date_abrogation')
			}
		];

		this.menuItems = [
			{
				label: this.translate.instant('common.export_action_label'),
				icon: 'pi pi-download',
				command: () => {
					this.exportTextes(false);
				}
			},
			{
				label: this.translate.instant('textes.export_with_articles'),
				icon: 'pi pi-download',
				command: () => {
					this.exportTextes(true);
				}
			},
		];
	}

	ngOnInit(): void {
		let previousStateName = this.uiRouterGlobals.transitionHistory.peekTail()?.$from().name;
		this.comingFromDashboard = previousStateName == 'abonne-courant.tableau-de-bord'

		let focusedTextId = this.storageService.getForCurrentState('focusedTextId', undefined, true);

		// Si on revient d'une évaluation on scrolle à la position du texte évalué
		if (focusedTextId && previousStateName == 'abonne-courant.evaluations.liste.single') {
			let start = Date.now();

			setTimeout(() => {
				let handle = setInterval(() => {
					if (Date.now() - start > 3000) {
						clearInterval(handle);
						return;
					}

					let row = (this.table.el.nativeElement as HTMLElement).querySelector(`[data-txt-id="${focusedTextId}"]`);

					if (row) {
						clearInterval(handle);
						row.scrollIntoView({behavior: 'instant', block: 'nearest'});
					}
				}, 300)
			});
		}

		this.canEdit =
			this.me.uti_administrateur
			|| this.sites.some((site: Site) => site.nac_id == 2);

		this.siteNames = this.sites.map((site: Site) => site.sit_libelle).join(', ');
		this.siteIds = this.sites.map((site: Site) => site.sit_id);
		this.siteService.getMergedNomenclatureDiagnostics(this.abonne_courant.abo_id,  this.sites.map(sit => sit.sit_id))
		.subscribe((nomenclature: Nomenclature) => {
			this.mergedNomenclatureDiagnostics = nomenclature;
		})

		if (this.comingFromDashboard) {
			if (this.initialStaCode != undefined) {
				this.params.sta_code = this.initialStaCode;
			}
			else if (this.initialArticleEvaCode != undefined) {
				this.params.eva_code_article = this.initialArticleEvaCode;
			}
			else if (this.select_unstatused_texts) {
				this.params.non_statues = true;
			}
			// On ne récupère pas les paramètres sauvegardés
		}
		else {
			this.getParamSet();

			if (this.initialStaCode != undefined) {
				this.params.sta_code = this.initialStaCode;
			}
			if (this.initialArticleEvaCode != undefined) {
				this.params.eva_code_article = this.initialArticleEvaCode;
			}
			if (this.select_unstatused_texts) {
				this.params.non_statues = true;
			}
			this.showAdvancedFilters = this.areAdvancedFiltersVisible();
		}

		if (this.nomenclature_selection_key) {
			Object.assign(this.params, extractNomenclatureElementIdFromKey(this.nomenclature_selection_key));
		}
		
		// Si le filtre par statut n'a pas de valeur et qu'on affiche les évaluations on met sta_code à applicable
		// afin de seulement montrer les textes intéressants.
		if (this.etapeConformite == 'evaluations' && this.params.sta_code === null) {
			this.params.sta_code = 'app';
		}
	}

	getParamSet() {
		let tableParams = this.storageService.getForCurrentState('tableParams', undefined, true);

		if (tableParams) {
			for (let prop in this.params) {
				if (typeof tableParams[prop] != 'undefined') this.params[prop] = tableParams[prop];
			}
			if (tableParams.first) this.table.first = tableParams.first;
			if (tableParams.rows) this.rows = tableParams.rows;

			if (tableParams.date_debut_reevaluation && tableParams.date_fin_reevaluation) {
				this.dateReevaluationRange = [
					new Date(tableParams.date_debut_reevaluation),
					new Date(tableParams.date_fin_reevaluation)
				]
			}
			if (tableParams.nomenclature_selection_key) {
				this.nomenclature_selection_key = tableParams.nomenclature_selection_key;
			}
		}
		this.table.rows = this.rows;
	}

	saveParamSet() {
		if (this.table) {
			let tableParams = Object.assign({}, this.params, {
				first: this.table.first,
				rows: this.table.rows,
				nomenclature_selection_key: this.nomenclature_selection_key,
				date_debut_sortie: undefined,
				date_fin_sortie: undefined
			});
			this.storageService.setForCurrentState('tableParams', tableParams, true);

			const uiParams: any = Object.assign({}, this.stateService.params, this.params, {
				sit_actif: undefined,
			});
			this.stateService.go('.', uiParams, { reload: false, location: 'replace', inherit: false });
		}
	}

	areAdvancedFiltersVisible() {
		return this.params.date_debut_reevaluation
			|| this.params.date_fin_reevaluation
			|| this.params.date_debut_sortie
			|| this.params.date_fin_sortie
			|| this.params.hors_diagnostic
			|| this.params.prive;
	}

	toggleAdvancedFilters() {
		this.showAdvancedFilters = !this.showAdvancedFilters;
	}

	refresh(stayOnPage?: boolean) {
		if (!stayOnPage) this.table._first = 0;
		this.table.onLazyLoad.emit(this.table.createLazyLoadMetadata());
	}

	loadTextes(event: TableLazyLoadEvent) {
		// https://github.com/primefaces/primeng/issues/8465#issuecomment-617887919
		Promise.resolve(null).then(() => {
			this.loading = true;
		});

		let sta_code: StatutRecolement = this.params.sta_code;
		const tmpParams = Object.assign({}, event, this.params);

		this.saveParamSet();

		if (String(tmpParams.order_by).startsWith('txt_date')) {
			tmpParams.sortOrder = 0; // décroissant
		}
		else if (tmpParams.order_by == 'pertinence') {
			delete tmpParams.order_by;
		}
		else {
			tmpParams.sortOrder = 1;
		}

		this.siteService.getEvaluationsTextes(this.abonne_courant.abo_id, this.siteIds, tmpParams)
		.subscribe({
			next: ({ total, textes }) => {
				this.totalRecords = total;
				this.values = textes;

				let pristineValues = structuredClone(textes);
				this.pristineValues = pristineValues;

				for (let pristineTexte of pristineValues) {
					for (let recolement of pristineTexte.conformite) {
						recolement.ctx_commentaire ??= null;
					}
				}

				this.remainingRecolementsArticleBySiteIdByTextId = {};

				for (let pristineTexte of pristineValues) {
					for (let evaluation of pristineTexte.conformite) {
						evaluation.ctx_commentaire ??= null;
					}
					for (let article of pristineTexte.articles) {
						for (let evaluationArticle of article.conformite) {
							this.remainingRecolementsArticleBySiteIdByTextId[evaluationArticle.sit_id] ??= {};

							if (evaluationArticle.sta_code != 'inc' || article.art_abrogation) {
								continue;
							}

							let remainingRecolementCount = this.remainingRecolementsArticleBySiteIdByTextId[evaluationArticle.sit_id];
							remainingRecolementCount[pristineTexte.txt_id] ??= 0;
							remainingRecolementCount[pristineTexte.txt_id] += 1;
						}
					}
				}

				// Si on revient sur la liste paginée et que la page en question est vide, on reset
				if (this.values.length === 0 && this.table.first != 0) {
					this.refresh();
				}

				this.expandedRows = Object.fromEntries(
					this.values.map((texte: TexteEvaluation) => [
						//identifiant de ligne
						texte.txt_id,
						//expansion
						true
					])
				);
			}
		})
		.add(() => { this.loading = false; });
	}

	exportTextes(articles = false){
		this.exportLoading = true;

		const tmpParams = Object.assign({}, this.params);
		tmpParams.avec_articles = articles;

		if (String(tmpParams.order_by).startsWith('txt_date')) {
			tmpParams.sortOrder = 0; // décroissant
		}
		else if (tmpParams.order_by == 'pertinence') {
			delete tmpParams.order_by;
		}
		else {
			tmpParams.sortOrder = 1;
		}

		this.siteService.exportEvaluationsTextes(this.abonne_courant.abo_id, this.siteIds, tmpParams)
		.subscribe(response => {
			createDownloadFromHttpResponse(response);
		})
		.add(() => {
			this.exportLoading = false;
		});
	}

	nomenclatureSelectionChange(event: any) {
		this.params.fam_id = null;
		this.params.thm_id = null;
		this.params.rub_id = null;
		if (event) {
			this.params.fam_id = event.fam_id;
			this.params.thm_id = event.thm_id;
			this.params.rub_id = event.rub_id;
		}
		if (this.nomenclatureFilter) {
			this.nomenclature_selection_key = this.nomenclatureFilter.key;
		}
		else {
			this.nomenclature_selection_key = undefined;
		}
		this.refresh();
	}

	setReevaluationDates(dateRange?: any) {
		this.params.date_debut_reevaluation = null;
		this.params.date_fin_reevaluation = null;
		if (dateRange && dateRange[1]) {
			this.params.date_debut_reevaluation = simpleDateToString(dateRange[0], true);
			this.params.date_fin_reevaluation = simpleDateToString(dateRange[1], true);
		}
		else {
			this.dateReevaluationRange = null;
		}
		this.refresh();
	}

	setReleaseDates(dateRange?: any) {
		this.params.date_debut_sortie = null;
		this.params.date_fin_sortie = null;
		if (dateRange && dateRange[1]) {
			this.params.date_debut_sortie = simpleDateToString(dateRange[0], true);
			this.params.date_fin_sortie = simpleDateToString(dateRange[1], true);
		}
		else {
			this.dateSortieRange = null;
		}
		this.refresh();
	}

	resetFilters() {
		this.params = structuredClone(this._defaultParams);
		this.dateReevaluationRange = null;
		this.dateSortieRange = null;
		this.nomenclatureFilter = null;
		this.refresh();
	}

	goToEvaluationTexte(texteConformite: TexteEvaluation) {
		// On sauvegarde le texte vers lequel scroller lorsque l'utilisateur reviendra sur la liste
		this.storageService.setForCurrentState('focusedTextId', texteConformite.txt_id, true);

		this.stateService.go('.single', {
			evaluable: texteConformite.evaluable,
			txt_id: texteConformite.txt_id,
			//texteEvaluation: texteConformite,
			sit_ids: this.siteIds
		});
	}

	canBeEvaluated(texteEvaluation: TexteEvaluation) {
		const areSomeRecolementsApplicable = texteEvaluation.conformite.some(evaluation => evaluation.sta_code == 'app');
		return areSomeRecolementsApplicable && !texteEvaluation.txt_abrogation;
	}

	openCommentaireTexteDialog(texte: Texte) {
		this.dialogService.open(CommentaireTexteDialogComponent, {
			header: this.translate.instant('textes.commentaire_of_text', {text: texte.txt_reference}),
			styleClass: 'max-w-120rem p-dialog-high-height',
			data: {
				texte: texte,
			}
		});
	}

}

@NgModule({
	imports: [
		CommonModule,
		GlobalModule,
		UIRouterModule,
		FormsModule,
		AccordionModule,
		ButtonModule,
		CalendarModule,
		CheckboxModule,
		IconFieldModule,
		InputIconModule,
		MenuModule,
		PanelModule,
		TableModule,
		ToolbarModule,
		FamilleSelectorModule,
		ThemeSelectorModule,
		RubriqueSelectorModule,
		NomenclatureSelectorModule,
		DropdownModule,
		StatutRecolementSelectorModule,
		EvaluationSelectorModule,
		TooltipModule,
		InputTextareaModule
	],
	exports: [ListeTextesConformiteComponent],
	declarations: [ListeTextesConformiteComponent]
})
export class ListeTextesConformiteModule { }
